import React from 'react';
import { Calendar, Clock, Info } from 'lucide-react';
import moment from 'moment-timezone';
import 'moment/locale/pt-br';

moment.locale('pt-br');

interface DrawDateFieldProps {
  drawDate: string | null;
  instantDraw: boolean;
  onChange: (value: string | null) => void;
}

const TIMEZONE = 'America/Sao_Paulo'; // GMT-3

const DrawDateField: React.FC<DrawDateFieldProps> = ({ drawDate, instantDraw, onChange }) => {
  // Only show if instant_draw is false
  if (instantDraw) return null;

  // Convert UTC to Brasília time for display
  const getLocalDateTimeValue = () => {
    if (!drawDate) return '';

    // Parse UTC date and convert to Brasília timezone
    const brasiliaTime = moment.utc(drawDate).tz(TIMEZONE);

    // Format for datetime-local input (YYYY-MM-DDTHH:mm)
    return brasiliaTime.format('YYYY-MM-DDTHH:mm');
  };

  // Convert Brasília time to UTC for storage
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const localValue = e.target.value;
    if (!localValue) {
      onChange(null);
      return;
    }

    // Parse as Brasília time and convert to UTC
    const utcTime = moment.tz(localValue, TIMEZONE).utc();

    // Store as ISO string in UTC
    onChange(utcTime.toISOString());
  };

  // Get minimum datetime (now + 1 hour in Brasília time)
  const getMinDateTime = () => {
    const minTime = moment.tz(TIMEZONE).add(1, 'hour');
    return minTime.format('YYYY-MM-DDTHH:mm');
  };

  return (
    <div className="space-y-2">
      <label htmlFor="drawDate" className="block text-xs font-bold uppercase tracking-wider text-tinta">
        <Calendar className="inline w-4 h-4 mr-1 text-bordo" />
        Data e Hora do Sorteio
      </label>

      <div className="relative">
        <input
          type="datetime-local"
          id="drawDate"
          value={getLocalDateTimeValue()}
          onChange={handleDateChange}
          min={getMinDateTime()}
          className="mt-1 block w-full bg-white border border-tinta/15 rounded-lg px-3 py-2.5 text-sm text-tinta placeholder:text-tinta-soft/70 placeholder:italic focus:outline-none focus:ring-2 focus:ring-bordo/40 focus:border-bordo transition-colors"
          required={!instantDraw}
        />
        <Clock className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-tinta-soft/60 pointer-events-none" />
      </div>

      <div className="flex items-start gap-2 bg-tinta/5 border border-tinta/15 rounded-lg p-3 text-sm text-tinta-soft">
        <Info className="w-4 h-4 text-bordo flex-shrink-0 mt-0.5" />
        <div className="text-xs">
          <p className="font-bold text-tinta mb-1">Horário de Brasília</p>
          <p className="italic">
            Defina a data e hora em que o sorteio será realizado. Os participantes
            verão esta informação após se inscreverem. Você poderá executar o sorteio
            manualmente através do painel admin.
          </p>
        </div>
      </div>

      {drawDate && (
        <div className="bg-musgo/10 border border-musgo/30 rounded-lg p-3">
          <p className="text-xs text-musgo-dark">
            <strong className="font-bold">Sorteio agendado para:</strong>{' '}
            {moment.utc(drawDate).tz(TIMEZONE).format('DD [de] MMMM [de] YYYY [às] HH:mm')}
          </p>
        </div>
      )}
    </div>
  );
};

export default DrawDateField;
